import classnames from 'classnames';
import React, { useState } from 'react';

import { Fab, Dialog, DialogContent, DialogActions, Button, Typography } from '@material-ui/core';
import { createStyles, withStyles, withTheme } from '@material-ui/core/styles';

import { isMobile } from '../../utils';
import { isInternalAudio } from '../../utils/internalAudio';

import AvaMic from '../../../assets/icons/mic-ava.png';
import AvaMicInternal from '../../../assets/icons/mic-ava-internal.png';
import AvaMicInternalOnly from '../../../assets/icons/mic-ava-internal-only.png';
import Headset from '../../../assets/images/headphones.png';
import MicrophoneDenied from '../../components/Modals/MicrophoneDenied';
import { hasScribe } from '../../utils/status';

const styles = createStyles(theme => ({
  headsetPopupContent: {
    fontSize: 18,
    textAlign: 'center',
  },
  headsetPopupCTA: {
    backgroundColor: theme.palette.ava.blue,
    color: 'white',
    display: 'flex',
    flexGrow: 1,
    margin: 20,
    marginBottom: 10,
    marginTop: 0,
  },
  headsetPopupImg: {
    display: 'block',
    margin: 'auto',
    marginBottom: 30,
    marginTop: 30,
  },
  headsetPopupPaper: {
    maxWidth: 400,
  },
  headsetPopupTitle: {
    fontSize: 22,
    fontWeight: 600,
    marginBottom: 20,
    textAlign: 'center',
  },
  audioIndicatorBtn: {
    marginBottom: '10px',
    WebkitAppRegion: 'no-drag',
  },
  audioIndicatorCCBtn: {
    position: 'fixed',
    marginBottom: 'unset',
    top: '50%',
    left: 60,
    transform: 'translate(-50%, -50%)',
    minHeight: 30,
    minWidth: 30,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    transition: 'all 120ms ease-in 0s',
  },
}));

const AudioTranscoderUI = React.memo(function AudioTranscoderUI(props) {
  const {
    audioParamsSent,
    availableMics,
    bluetooth,
    classes,
    className,
    electronCaptionMode,
    fullScreen,
    inTwilioCalls,
    showMinimalMic,
    isRecording,
    micIdSelected,
    microphoneDenied,
    selectedCaptions,
    startRecord,
    status,
    stopRecord,
    t,
    theme,
    user,
  } = props;

  const [headsetPopupShown, setHeadsetPopupShown] = useState(false);

  let background = theme.palette.ava.grey;
  let shadowColor;
  if (isRecording && audioParamsSent) {
    if (hasScribe(status)) {
      background = theme.palette.ava.green;
    } else if (selectedCaptions === undefined || selectedCaptions === 'premium') {
      background = theme.palette.ava.gradientBlue;
      shadowColor = theme.palette.ava.darkBlue;
    } else {
      background = theme.palette.ava.blue;
    }
  }

  if (!shadowColor) shadowColor = background;
  if (inTwilioCalls || (fullScreen && (!window.isElectron || !electronCaptionMode))) {
    return <div />;
  }

  let micImg = AvaMic;
  const mic = availableMics && availableMics.find(m => m.value === micIdSelected);
  if (mic) {
    const isInternalMic = isInternalAudio(mic);
    micImg = isInternalMic ? AvaMicInternal : AvaMic;
  }
  const micStyle = {
    background,
    display: 'flex',
    flexDirection: 'column',
    color: 'white',
    transition: 'all 120ms ease-in 0s',
  };

  if (showMinimalMic) {
    micStyle.width = 20;
    micStyle.height = 20;
    micStyle.minHeight = 20;
  }

  return (
    <div
      className={classnames(classes.audioIndicatorBtn, className, {
        [classes.audioIndicatorCCBtn]: window.isElectron && electronCaptionMode,
      })}
      style={{
        width: showMinimalMic ? 30 : 56,
        height: showMinimalMic ? 30 : 56,
      }}
    >
      <Fab
        color="secondary"
        className="audio-indicator"
        style={micStyle}
        onMouseDown={e => e.stopPropagation()}
        onClick={() => (isRecording ? stopRecord() : startRecord())}
      >
        {micImg && (
          <img
            src={micImg}
            alt="ava_mic"
            style={{
              transition: 'all 200ms ease-in',
              height: showMinimalMic ? 0 : 30,
              opacity: showMinimalMic ? 0 : 1,
            }}
          />
        )}
      </Fab>
      {!electronCaptionMode && (
        <div
          style={{
            background,
            color: 'white',
            borderRadius: 10,
            textAlign: 'center',
            margin: 'auto',
            paddingLeft: 5,
            paddingRight: 5,
            width: 'fit-content',
            marginTop: -1,
          }}
        >
          {isRecording && audioParamsSent ? 'ON' : 'OFF'}
        </div>
      )}
      {microphoneDenied && <MicrophoneDenied open={microphoneDenied} />}
      {/* The Headset Popup will show when the number of users in a convo is at least *3* */}
      <Dialog
        open={
          !headsetPopupShown &&
          !window.isElectron &&
          !electronCaptionMode &&
          isRecording &&
          audioParamsSent &&
          !isMobile() &&
          !bluetooth &&
          (status.audioStreams || []).filter(as => as.avaId !== user.avaId).length > 1 &&
          status.conversationMode === 'public'
        }
        classes={{ paper: classes.headsetPopupPaper }}
      >
        <DialogContent>
          <img src={Headset} alt="headphones" className={classes.headsetPopupImg} />
          <Typography className={classes.headsetPopupTitle}>{t('conversation.headsetPopUp.title')}</Typography>
          <Typography className={classes.headsetPopupContent}>{t('conversation.headsetPopUp.content1')}</Typography>
          <Typography className={classes.headsetPopupContent}>{t('conversation.headsetPopUp.content2')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHeadsetPopupShown(true)} className={classes.headsetPopupCTA} autoFocus>
            {t('conversation.headsetPopUp.cta')}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
});

export default withStyles(styles)(withTheme(AudioTranscoderUI));
